// @flow
import { createRef, useState } from "react";
import { useSelector } from "react-redux";
import { TextInput } from "@nested/brand";
import {
  Heading,
  QuestionWrapper,
  QuestionDescription,
  PrivacyPolicyCheckbox,
} from "../components";
import { type QuestionProps } from "../components/MultiStageForm";
import { useDesktopFocus } from "../hooks/useDesktopFocus";
import {
  composeValidators,
  validateEmail,
  validateLength,
  validatePresence,
} from "./validators";

const validateNameAndEmail = async (values = {}) => {
  const name = await validatePresence(
    "name",
    "Please enter your name",
  )(values?.name);
  const email = await composeValidators(
    validatePresence("email", "Please enter a valid email address"),
    validateEmail("email", "Please enter a valid email address"),
  )(values?.email);

  return { ...name, ...email };
};

const validateAll = async (values = {}) => {
  const name = await validatePresence(
    "name",
    "Please enter your name",
  )(values?.name);
  const email = await composeValidators(
    validatePresence("email", "Please enter a valid email address"),
    validateEmail("email", "Please enter a valid email address"),
  )(values?.email);
  const phone = await composeValidators(
    validatePresence("phone", "Please enter a valid phone number"),
    validateLength("phone", 2, 20, "Please enter a valid phone number"),
  )(values?.phone);

  return { ...name, ...email, ...phone };
};

type ContactDetailsProps = {
  ...QuestionProps<"contactDetails">,
};

export const ContactDetails = ({
  ActionButtons,
  BackButton,
  initialValue,
  next,
  validationErrors,
}: ContactDetailsProps) => {
  const [name, setName] = useState(initialValue?.name || "");
  const [email, setEmail] = useState(initialValue?.email || "");
  const [phone, setPhone] = useState(initialValue?.phone || "");
  const [privacyPolicyConsent, setPrivacyPolicyConsent] = useState(
    initialValue?.privacyPolicyConsent || false,
  );
  const ref = createRef();
  useDesktopFocus(ref);

  const formState = useSelector((state) => state.onboardingForm);
  const requirePhone =
    formState.customerIntent === "ALREADY_ON_THE_MARKET" ||
    formState.customerIntent === "THINKING_ABOUT_IT";
  const highIntent =
    formState.urgency === "LESS_THAN_THREE_MONTHS" ||
    formState.customerIntent === "ALREADY_ON_THE_MARKET";

  const validate = requirePhone ? validateAll : validateNameAndEmail;

  return (
    <>
      <QuestionWrapper
        next={() =>
          next({ email, name, phone, privacyPolicyConsent }, validate)
        }
        dataTest="contact-details-question"
      >
        <BackButton />
        <Heading>{highIntent ? "A bit about you" : "Final step"}</Heading>
        <QuestionDescription>So we can help with your sale</QuestionDescription>

        <TextInput
          className="fs-exclude"
          data-test="contact-details-question:name"
          errorMessage={validationErrors.name}
          hasErrors={Boolean(validationErrors.name)}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          ref={ref}
          value={name}
        />
        <TextInput
          className="fs-exclude"
          css="margin-top: 15px;"
          data-test="contact-details-question:email"
          errorMessage={validationErrors.email}
          hasErrors={Boolean(validationErrors.email)}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          type="email"
          value={email}
        />
        <TextInput
          className="fs-exclude"
          css="margin-top: 15px;"
          data-test="contact-details-question:phone"
          errorMessage={validationErrors.phone}
          hasErrors={Boolean(validationErrors.phone)}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone number"
          type="phone"
          value={phone}
        />
        <PrivacyPolicyCheckbox
          css="margin-top: 30px;"
          checked={privacyPolicyConsent}
          onChange={setPrivacyPolicyConsent}
          labelCopy="Send me additional emails about my home, the area and the housing market"
        />
        <ActionButtons />
      </QuestionWrapper>
    </>
  );
};
